<template>
  <modal-wrap
    :id="id"
    ref="integrationsViskanSettings"
    :name="modalName"
    :header-class="'integrations-viskan-settings-header'"
    :hide-footer="true"
    body-class="integrations-viskan-settings-modal"
    size="md"
  >
    <template #modal-title>
      <div class="d-flex justify-content-between align-items-center">
        <h4 class="mb-0">
          {{ modalTitle }}
        </h4>
      </div>
    </template>
    <template #default>
      <div class="form-group">
        <p>Click Connect to authorize with your Visma/Viskan account. You will be redirected to Visma Connect to grant access.</p>
      </div>
      <div class="form-group">
        <button type="button" class="btn create-btn btn-green" :disabled="isButtonDisabled" @click="connect">
          Connect
        </button>
      </div>
    </template>
  </modal-wrap>
</template>

<script lang="ts">
import type { ViskanIntegrationModal } from '@/types/Models/Integration'
import { notify } from '@kyvg/vue3-notification'
import { defineComponent, ref, computed, type ComputedRef, type Ref } from 'vue'
import { VISKAN } from '@/const/IntegrationTypes'
import { VISKAN_SETTINGS } from '@/const/ModalTypes'
import { useCustomersIntegrationsStore } from '@/store/customers/integrations'
import { useVueNextModalStore } from '@/store/vueNextModal'
import ModalWrap from '@/views/Modals/ModalWrap.vue'

export default defineComponent({
  name: 'IntegrationsViskanSettingsModal',
  components: {
    ModalWrap,
  },
  props: {
    id: {
      type: String,
      required: true,
    },
  },
  setup() {
    const modalStore = useVueNextModalStore()
    const customersIntegrationsStore = useCustomersIntegrationsStore()
    const isButtonDisabled: Ref<boolean> = ref(false)

    const modalTitle = 'Viskan Integration'
    const modalName = VISKAN_SETTINGS

    const modalData: ComputedRef<ViskanIntegrationModal | null> = computed(
      () => modalStore.modalPropsByName(modalName) || null,
    )
    const customerId: ComputedRef<string> = computed(() => modalData.value?.customerId || '')

    const connect = () => {
      if (isButtonDisabled.value || !customerId.value) return
      isButtonDisabled.value = true

      customersIntegrationsStore
        .connect({
          customerId: customerId.value,
          data: {
            client_internal_id: customerId.value,
            marketplace: VISKAN,
            import_type: 'scheduled',
            import_interval: 60,
            active: false,
            default_weight: 500,
            default_length: 34,
            default_width: 24,
            default_height: 7,
            credentials: {},
          },
        })
        .then((response: any) => {
          const integrationId = response?.data?.id
          if (!integrationId) {
            notify({ group: 'error', text: 'Failed to create integration' })
            isButtonDisabled.value = false
            return
          }
          return customersIntegrationsStore.runActions({
            customerId: customerId.value,
            id: integrationId,
            action: 'connect',
            params: {},
          })
        })
        .then((response: any) => {
          if (!response) {
            isButtonDisabled.value = false
            return
          }
          if (response?.url) {
            window.location.href = response.url
          } else {
            notify({ group: 'error', text: response?.message || 'Failed to get authorization URL' })
            isButtonDisabled.value = false
          }
        })
        .catch(() => {
          isButtonDisabled.value = false
        })
    }

    return {
      isButtonDisabled,
      modalName,
      modalTitle,
      connect,
    }
  },
})
</script>

